import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Checkbox } from '@aok/ui-elements'
import { Picture } from '@aok/image'

import M047QuestionLayout from '../M047QuestionLayout/M047QuestionLayout'
import M047ImagePerQuestion from '../M047ImagePerQuestion/M047ImagePerQuestion'
import { ImageShape, ImageType } from '../M047Constants'

import createRandomID from '../../../../utils/createRandomID'
import defaultProps from '../../../../utils/propTypes/defaultProps'

const M047MultiSelection = ({
  navigateToNextPage,
  navigateToPreviousPage,
  onChangeCallback,
  headline,
  image,
  icon,
  options,
  settings,
}) => {
  const hasSelectedValue = options?.some((option) => option.selected === true)
  const showImagePerQuestion =
    !settings?.imagePerAnswer &&
    ((image && settings?.imageType === ImageType.Picture) || (icon && settings?.imageType === ImageType.Icon))
  const showImagePerAnswer = settings?.imagePerAnswer && options.every((option) => option.image !== undefined)
  const checkboxGroupName = useMemo(() => createRandomID('cb-group'), [])

  const handleCheckboxChange = (event) => {
    const updatedOptions = options.map((option) => {
      if (event.target.name === option?.id) {
        return { ...option, selected: event.target.checked }
      } else {
        return option
      }
    })

    onChangeCallback(updatedOptions)
  }

  return (
    <M047QuestionLayout
      headline={headline}
      onNextButtonClick={navigateToNextPage}
      onPreviousButtonClick={navigateToPreviousPage}
      nextButtonDisabled={!hasSelectedValue}
    >
      <div className="flex flex-col 600:items-center 600:flex-row 600:gap-x-16 720:gap-x-24 1000:gap-x-32">
        {/* Image Container */}
        {showImagePerQuestion && (
          <div
            className={`w-full shrink-0 ${
              settings?.imageType === ImageType.Icon || settings?.imageShape === ImageShape.Circle
                ? '600:w-160'
                : '600:w-1/2'
            }`}
          >
            <M047ImagePerQuestion {...settings} image={image} icon={icon} />
          </div>
        )}

        {/* Checkbox Container */}
        <fieldset
          className={`
            flex-grow 600:self-center flex flex-col gap-y-24 
            ${
              showImagePerQuestion
                ? 'mt-32 600:mt-0'
                : showImagePerAnswer
                ? `grid grid-cols-1 600:grid-cols-2 gap-24 600:gap-y-40 1000:gap-x-32 ${
                    options.length > 2 ? '840:grid-cols-3' : '1000:grid-cols-3'
                  }`
                : '840:grid 840:grid-cols-2 840:gap-24 1000:gap-x-32'
            }
          `}
        >
          <legend className="sr-only">{headline?.text}</legend>
          {options.map((option) => (
            <div
              className={`w-full ${
                showImagePerAnswer
                  ? 'flex items-center gap-24 600:flex-col 600:gap-x-0 600:gap-y-16 600:justify-center 840:justify-start 840:gap-y-24'
                  : ''
              }`}
              key={option?.id}
            >
              {showImagePerAnswer && (
                <div className="flex-shrink-0 flex-grow-0 justify-self-start">
                  <Picture
                    {...option?.image}
                    imgClassName="w-80 h-80 rounded-full 600:w-112 600:h-112 1000:w-128 1000:h-128"
                  />
                </div>
              )}
              <Checkbox
                id={option?.id}
                label={option?.text}
                name={option?.id}
                variant="structure"
                inputProps={{ checked: option.selected }}
                onChange={handleCheckboxChange}
                checkboxGroupName={checkboxGroupName}
              />
            </div>
          ))}
        </fieldset>
      </div>
    </M047QuestionLayout>
  )
}

M047MultiSelection.propTypes = {
  navigateToNextPage: PropTypes.func,
  navigateToPreviousPage: PropTypes.func,
  onChangeCallback: PropTypes.func,
  headline: PropTypes.shape({
    tag: PropTypes.string,
    text: PropTypes.string,
  }),
  image: PropTypes.shape({
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        breakpoint: PropTypes.number,
      })
    ),
    alt: PropTypes.string,
  }),
  icon: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      weight: PropTypes.number,
      image: PropTypes.shape({
        variants: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            breakpoint: PropTypes.number,
          })
        ),
        alt: PropTypes.string,
      }),
      selected: PropTypes.bool,
    })
  ),
  settings: PropTypes.shape({
    imagePerAnswer: PropTypes.bool,
    imageType: PropTypes.oneOf(Object.values(ImageType)),
    imageShape: PropTypes.oneOf(Object.values(ImageShape)),
  }),
}

M047MultiSelection.defaultProps = {
  navigateToNextPage: () => {},
  navigateToPreviousPage: () => {},
  onChangeCallback: () => {},
  headline: {
    text: defaultProps.HEADLINE,
  },
  options: [],
}

export default M047MultiSelection
